// lens.ts — the refine trainer's per-view lens blur (rstrain src/lens.rs
// `lens_cov` / `blurred`), mirrored for the Viewer's "Image Degradation"
// toggle, plus the keyframe undistortion that puts the photo in the same
// pinhole image the trainer blurs (core/camera undistort_map.go).
//
// The trainer never deblurs a frame: it renders each view blurred as the
// camera saw it. Per gaussian, at camera-frame position t (OpenCV axes: x
// right, y down, z forward) with projection Jacobian J (2×3):
//   - defocus:  σ = α·f·|1/z − ρ| px, an isotropic E_d = α²(1/z−ρ)²·diag(fx², fy²);
//   - motion:   the point moves by d = φ×t + δ over the exposure, smearing
//               its image along u = J·d px, a box of covariance u·uᵀ/12;
//   - Σ' = Σ + E_d + u·uᵀ/12, opacity × sqrt(det Σ / det Σ').
// The vertex shader (shaders.ts) evaluates the same expressions; project.ts
// is its CPU twin and calls lensCov/blurred so vitest checks the shader's
// arithmetic against these.

import type { PinholeIntrinsics } from './camera';

// Lens is one view's learned lens: focus plane rho (inverse depth, 1/m; 0 =
// infinity), aperture coefficient alpha (m), and the exposure path phi
// (rad) / delta (m) in camera axes (the camera's rotation and translation
// over the exposure).
export interface Lens {
  rho: number;
  alpha: number;
  phi: [number, number, number];
  delta: [number, number, number];
}

// LensCov is one gaussian's blur covariance [e00, e01, e11] (OpenCV pixel
// axes, y down), its smear u (px) and 1/z − rho.
export interface LensCov {
  e: [number, number, number];
  u: [number, number];
  ed: number;
}

// lensCov is rstrain lens.rs `lens_cov`: the blur covariance at camera point
// t with projection Jacobian j (row-major 2×3, j[1] = j[3] = 0).
export function lensCov(l: Lens, fx: number, fy: number, j: ArrayLike<number>, t: ArrayLike<number>): LensCov {
  const ed = 1 / t[2] - l.rho;
  const ad = l.alpha * ed;
  const kx = ad * fx;
  const ky = ad * fy;
  const d0 = l.phi[1] * t[2] - l.phi[2] * t[1] + l.delta[0];
  const d1 = l.phi[2] * t[0] - l.phi[0] * t[2] + l.delta[1];
  const d2 = l.phi[0] * t[1] - l.phi[1] * t[0] + l.delta[2];
  const u0 = j[0] * d0 + j[2] * d2;
  const u1 = j[4] * d1 + j[5] * d2;
  return {
    e: [kx * kx + (u0 * u0) / 12, (u0 * u1) / 12, ky * ky + (u1 * u1) / 12],
    u: [u0, u1],
    ed,
  };
}

// blurred is rstrain lens.rs `blurred`: the blurred covariance, its
// determinant and the opacity scale sqrt(det / det') from the unblurred
// covariance cv (determinant det > 0).
export function blurred(cv: ArrayLike<number>, det: number, lc: LensCov): { cov: [number, number, number]; det: number; opScale: number } {
  const c: [number, number, number] = [cv[0] + lc.e[0], cv[1] + lc.e[1], cv[2] + lc.e[2]];
  const detb = c[0] * c[2] - c[1] * c[1];
  return { cov: c, det: detb, opScale: Math.sqrt(det / detb) };
}

// pinholeJacobian is the OpenCV projection Jacobian at camera point t
// (row-major 2×3), as rstrain's compute_jacobian without its FOV clamp.
export function pinholeJacobian(fx: number, fy: number, t: ArrayLike<number>): [number, number, number, number, number, number] {
  const z = t[2];
  return [fx / z, 0, -(fx * t[0]) / (z * z), 0, fy / z, -(fy * t[1]) / (z * z)];
}

// LensReport is the part of a keyframe's blur report (api.ts ViewBlur) the
// lens is read from; structural so the splat stack stays free of api.ts.
export interface LensReport {
  phi: ArrayLike<number>;
  delta: ArrayLike<number>;
  focus_rho?: number;
  aperture_m?: number;
}

// hasLens reports whether a refinement's blur report carries the lens
// model's view (the Go side omits the lens fields when it ran without one;
// a positive aperture is the marker, since rho = 0 means infinity).
export function hasLens<T extends LensReport>(b: T | null | undefined): b is T & { aperture_m: number } {
  return !!b && typeof b.aperture_m === 'number' && b.aperture_m > 0;
}

// lensFromBlur is the keyframe's lens, or null when the refinement did not
// model one.
export function lensFromBlur(b: LensReport | null | undefined): Lens | null {
  if (!hasLens(b)) return null;
  return {
    rho: b.focus_rho ?? 0,
    alpha: b.aperture_m,
    phi: [b.phi[0], b.phi[1], b.phi[2]],
    delta: [b.delta[0], b.delta[1], b.delta[2]],
  };
}

// distortNormalized is camera.Intrinsics.Distort: Brown–Conrady (k1, k2,
// p1, p2) on normalized coordinates.
export function distortNormalized(k: Distortion, xn: number, yn: number): [number, number] {
  const r2 = xn * xn + yn * yn;
  const radial = 1 + k.k1 * r2 + k.k2 * r2 * r2;
  return [
    xn * radial + 2 * k.p1 * xn * yn + k.p2 * (r2 + 2 * xn * xn),
    yn * radial + k.p1 * (r2 + 2 * yn * yn) + 2 * k.p2 * xn * yn,
  ];
}

export interface Distortion {
  k1: number;
  k2: number;
  p1: number;
  p2: number;
}

export function hasDistortion(k: Distortion): boolean {
  return k.k1 !== 0 || k.k2 !== 0 || k.p1 !== 0 || k.p2 !== 0;
}

// undistortRGBA is core/camera NewUndistortMap + UndistortIntoWithMap: each
// pinhole pixel (same fx, fy, cx, cy, pixel centres at +0.5) samples the
// distorted source bilinearly where Distort lands it; pixels that land
// outside the source are transparent black. k must already be at the
// source's size (scaleIntrinsics). Returns a new w×h RGBA buffer.
export function undistortRGBA(src: Uint8ClampedArray, w: number, h: number, k: PinholeIntrinsics & Distortion): Uint8ClampedArray {
  const dst = new Uint8ClampedArray(w * h * 4);
  if (!hasDistortion(k)) {
    dst.set(src.subarray(0, w * h * 4));
    return dst;
  }
  for (let y = 0; y < h; y++) {
    const yn = (y + 0.5 - k.cy) / k.fy;
    for (let x = 0; x < w; x++) {
      const [xd, yd] = distortNormalized(k, (x + 0.5 - k.cx) / k.fx, yn);
      let fx = k.fx * xd + k.cx - 0.5;
      let fy = k.fy * yd + k.cy - 0.5;
      if (Number.isNaN(fx) || Number.isNaN(fy) || fx < -0.5 || fy < -0.5 || fx > w - 0.5 || fy > h - 0.5) continue;
      fx = Math.max(0, Math.min(fx, w - 1));
      fy = Math.max(0, Math.min(fy, h - 1));
      const x0 = Math.floor(fx), y0 = Math.floor(fy);
      const x1 = Math.min(x0 + 1, w - 1), y1 = Math.min(y0 + 1, h - 1);
      const ax = fx - x0, ay = fy - y0;
      const i00 = (y0 * w + x0) * 4, i10 = (y0 * w + x1) * 4, i01 = (y1 * w + x0) * 4, i11 = (y1 * w + x1) * 4;
      const o = (y * w + x) * 4;
      for (let c = 0; c < 4; c++) {
        const top = src[i00 + c] * (1 - ax) + src[i10 + c] * ax;
        const bot = src[i01 + c] * (1 - ax) + src[i11 + c] * ax;
        dst[o + c] = top * (1 - ay) + bot * ay;
      }
    }
  }
  return dst;
}
